"use client";

import { Routes } from "@/api";
import { UserNotification } from "@/services/users/schema";
import { formatPostDate } from "@/utils/format-date";
import { Card, CardContent, CardTitle } from "../ui/card";

import { NotificationType } from "@/config/notification";
import { HasUnreadNotificationsQuery } from "@/hooks/queries/user/use-has-unread-notifications-query";
import { cn } from "@/lib/utils";
import { PropsWithClassName } from "@/types";
import { capitalize } from "@/utils/capitalize";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";

function getBadgeColor(notificationType: NotificationType) {
  switch (notificationType) {
    case "message":
      return "bg-secondary text-secondary-foreground";
    case "subscribe":
    case "subscription-success":
      return "bg-warning text-warning-foreground";
    case "follow":
      return "bg-green-500 text-white";
    case "portfolio-change":
      return "bg-blue-500 text-white";
    default:
      return "bg-primary text-primary-foreground";
  }
}

function getRelativeUrl(url: string) {
  return url.replace(process.env.NEXT_PUBLIC_BASE_URL, "");
}

interface NotificationItemProps {
  readonly notification: UserNotification;
  readonly unread: boolean;
  readonly userId: string;
}
export function NotificationItem(
  props: PropsWithClassName<NotificationItemProps>,
) {
  const { notification } = props;
  const router = useRouter();

  const onNotificationClick = () => {
    const relativeUrl = getRelativeUrl(notification.payload.data.url);
    HasUnreadNotificationsQuery.invalidate(props.userId);

    router.push(relativeUrl);
  };

  return (
    <div
      onClick={onNotificationClick}
      className={"hover:cursor-pointer"}
      aria-label="notification-card"
    >
      <Card
        className={cn(
          "gap-2 rounded-none p-2",
          props.className,
          props.unread ? "bg-sidebar-accent" : undefined,
        )}
      >
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 pb-2">
          <div className="flex items-center gap-2">
            {notification.sender.image ? (
              <Avatar className="size-6">
                <Link
                  href={Routes.USER_PORTFOLIOS(notification.sender?.id ?? "")}
                  className="hover:cursor-pointer"
                >
                  {notification.sender?.image && notification.sender.name ? (
                    <AvatarImage
                      src={notification.sender.image}
                      alt={notification.sender.name}
                      width={24}
                      height={24}
                    />
                  ) : null}
                </Link>
                <AvatarFallback>
                  {notification.sender.name ?? "User Avatar"}
                </AvatarFallback>
              </Avatar>
            ) : null}
            {notification.sender.name}

            <span className="text-sm text-muted-foreground">&middot;</span>
            <span className="text-sm text-muted-foreground">
              {formatPostDate(notification.createdAt)}
            </span>
          </div>
          <Badge variant="default" className={getBadgeColor(notification.type)}>
            {capitalize(notification.type.split("-").join(" "))}
          </Badge>
        </CardTitle>
        <CardContent className="p-0">
          <div className="flex flex-col">
            <p className="text-wrap text-sm font-semibold text-foreground">
              {notification.payload.title}
            </p>
            <p className="block whitespace-pre-wrap text-wrap text-sm text-foreground/70">
              {notification.payload.body}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
